// src/pages/events/EventForm.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import eventService from '../../services/eventService';
import LayoutContainer from '../../components/LayoutContainer';
import AdminHeader from '../../components/AdminHeader';
import { capitalize, concat, filter, isEmpty, map, toUpper } from 'lodash';
import departmentService from '../../services/departmentService';
import fileUploadService from '../../services/fileUploadService';
import moment from 'moment';
import { toast } from 'react-toastify';

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventDetail, setEventDetail] = useState({});
  // const [eventLogo, setEventLogo] = useState('');
  // const [imageFile, setImageFile] = useState(null);
  // const [departments, setDepartments] = useState([]);

  const [events, setEvents] = useState([]);

  const fetchEvents = async () => {
    const response = await eventService.getEvents();
    setEvents(response.data);
  };

  const initialValues = id
    ? {
        ...eventDetail,
        event_date: moment(eventDetail?.event_date)?.format('YYYY-MM-DD'),
      }
    : {
        name: '',
        photo: '',
        event_date: '',
        city: '',
        active: true,
        kml: '',
      };

  const validationSchema = Yup.object({
    name: Yup.string().required('Event name is required'),
    city: Yup.string().required('City is required'),
    event_date: Yup.string().required('Event date is required'),
    // description: Yup.string().required('Description is required'),
  });

  // useEffect(() => {
  //   const fetchDepartment = async () => {
  //     const response = await departmentService.getDepartments();
  //     setDepartments(response.data);
  //   };
  //   fetchDepartment();
  // }, []);

  useEffect(() => {
    if (id) {
      const fetchEvent = async () => {
        const response = await eventService.getEvent(id);
        const data = response.data;
        setEventDetail(data);
      };
      fetchEvent();
    }
    fetchEvents();
  }, [id]);

  const handleSubmit = async (values) => {
    const isActive = values?.active;

    const getActiveEvent = (excludeId) => {
      return (
        filter(events, (data) => data?.id !== excludeId && data?.active) || []
      );
    };

    const updateActiveEvent = async (excludeId) => {
      const activeEvent = getActiveEvent(excludeId);
      if (activeEvent.length >= 1) {
        const [firstActiveEvent] = activeEvent;
        await eventService.updateEvent(
          firstActiveEvent.id,
          map(activeEvent, (data) => ({
            ...data,
            event_date: data?.event_date,
            active: false,
          }))
        );
      }
    };

    if (id) {
      if (isActive && getActiveEvent(id).length >= 1) {
        await updateActiveEvent(id);
      }
      await eventService.updateEvent(id, values);
    } else {
      if (isActive && filter(events, { active: true })?.length >= 1) {
        await updateActiveEvent();
      }
      await eventService.createEvent(values);
    }

    if (isActive && id) {
      localStorage?.setItem('event_id', id);
    }

    navigate('/events');
  };

  const handleFileChange = async (field, setFieldValue, file) => {
    if (field === 'kml') {
      const res = await fileUploadService?.handleUploadDoc(file);
      if (res?.status === 200) {
        setFieldValue(field, `${res?.data?.filename}`);
        // toast?.success('File uploaded successfully')
      }
    } else {
      try {
        const res = await fileUploadService?.handleFileUpload(file);
        if (res?.status === 200) {
          setFieldValue(field, `${res?.data?.filename}`);
        }
      } catch (e) {
        toast?.error(e?.response?.data?.message);
      }
    }
  };

  return (
    <LayoutContainer>
      <AdminHeader title={id ? 'EDIT EVENT' : 'ADD EVENT'} backTo='/events' />
      <Box
        className='page-content-wrapper scrollable'
        p={3}
        sx={{ display: 'flex', backgroundColor: '#efefef' }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            errors,
            touched,
          }) => (
            <form onSubmit={handleSubmit} style={{ width: '50%' }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3} sx={{ p: 1 }}>
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        EVENT NAME <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        placeholder='ENTER EVENT NAME'
                        name='name'
                        value={values.name}
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: { name: 'name', value: capitalizedValue },
                          });
                        }}
                        fullWidth
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.name && errors.name
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                      {touched.name && errors.name && (
                        <FormHelperText error>{errors.name}</FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        CITY <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Select
                        name='city'
                        value={values?.city || ''}
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: { name: 'city', value: capitalizedValue },
                          });
                        }}
                        fullWidth
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: '#f5f5f5',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.city && errors.city
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      >
                        {map(
                          ['DELHI', 'MUMBAI', 'CALCUTTA', 'BENGALURU'],
                          (option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {touched.city && errors.city && (
                        <FormHelperText style={{ color: '#D32F2F' }}>
                          {errors.city}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        EVENT DATE <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        placeholder='Select Event Date'
                        name='event_date'
                        type='date'
                        fullWidth
                        value={values?.event_date}
                        onChange={handleChange}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.event_date && errors.event_date
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                      {touched.event_date && errors.event_date && (
                        <FormHelperText error>
                          {errors.event_date}
                        </FormHelperText>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        EVENT LOGO:
                      </Typography>
                      <input
                        type='file'
                        accept='image/*'
                        onChange={(e) =>
                          handleFileChange(
                            'photo',
                            setFieldValue,
                            e.target.files[0]
                          )
                        }
                      />
                      {values?.photo && (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_URL}/images/${values?.photo}`}
                          alt='Event Logo'
                          width={50}
                          height={50}
                          style={{ borderRadius: '4px' }}
                        />
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        UPLOAD GEOJSON FILE:
                      </Typography>
                      <input
                        type='file'
                        accept='.geojson'
                        style={{ width: '30%' }}
                        onChange={(e) =>
                          handleFileChange(
                            'kml',
                            setFieldValue,
                            e.target.files[0]
                          )
                        }
                      />
                      <span style={{ fontSize: 'small' }}>
                        {' '}
                        {values?.kml ? (
                          <a
                            href={`${process.env.REACT_APP_IMAGE_URL}/mapfiles/${values?.kml}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ textDecoration: 'none' }}
                          >
                            {values?.kml}
                          </a>
                        ) : (
                          ''
                        )}
                      </span>
                      {/* {values?.kml && (
                        <img
                          src={values?.kml}
                          alt='Event Logo'
                          width={50}
                          height={50}
                          style={{ borderRadius: '4px' }}
                        />
                      )} */}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.active}
                            color='primary'
                            name='active'
                            onChange={(e) =>
                              setFieldValue('active', e?.target?.checked)
                            }
                          />
                        }
                        label='ACTIVE EVENT'
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          type='submit'
                          variant='contained'
                          sx={{
                            backgroundColor: '#be210b',
                            color: '#fff',
                            textTransform: 'none',
                            padding: '5px 15px',
                            '&:hover': {
                              backgroundColor: '#a2180a',
                            },
                          }}
                        >
                          {id ? 'UPDATE' : 'CREATE'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </form>
          )}
        </Formik>
      </Box>
    </LayoutContainer>
  );
};

export default EventForm;
